import { useId, type ChangeEvent } from "react";
import { AttachmentError, GALLERY_ACCEPT, type AttachmentState } from "../../shared/media/attachment.ts";
import { Spinner } from "../../shared/ui/Spinner/Spinner.tsx";
import styles from "./AttachmentField.module.css";

type AttachmentFieldProps = {
  state: AttachmentState;
  onPick: (file: File) => void;
  onRetry: () => void;
  onClear: () => void;
};

/**
 * «Добавить изображение» (Figma 1019:19170, Message / Attachment Actions 571:2389): камера или галерея.
 * Под кнопками — загрузка с прогрессом (08.05, Figma 1023:4951) или ошибка с повтором (08.06, Figma 1023:5040).
 * Кнопки «Выбрать GIF» из макета нет: GIF выбирается из галереи.
 */
export function AttachmentField({ state, onPick, onRetry, onClear }: AttachmentFieldProps) {
  const titleId = useId();

  function choose(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Сброс значения: тот же файл, выбранный ещё раз, тоже вызовет `change`.
    event.target.value = "";
    if (file) onPick(file);
  }

  return (
    <section className={styles.field} aria-labelledby={titleId}>
      <div className={styles.header}>
        <h3 id={titleId} className={styles.title}>
          Добавить изображение
        </h3>
        <span className={styles.limit}>до 5 MB</span>
      </div>
      <div className={styles.actions}>
        <label className={styles.action}>
          Сделать фото
          <input type="file" className={styles.input} accept="image/*" capture="environment" onChange={choose} />
        </label>
        <label className={styles.action}>
          Выбрать из галереи
          <input type="file" className={styles.input} accept={GALLERY_ACCEPT} onChange={choose} />
        </label>
      </div>
      <AttachmentStatus state={state} onRetry={onRetry} onClear={onClear} />
    </section>
  );
}

function AttachmentStatus({ state, onRetry, onClear }: Omit<AttachmentFieldProps, "onPick">) {
  switch (state.status) {
    case "empty":
      return null;
    case "preparing":
    case "uploading":
    case "processing":
      return (
        <p className={styles.status} role="status">
          <Spinner size={16} />
          {state.status === "uploading" ? `Загрузка вложения… ${Math.round(state.progress * 100)}%` : "Загрузка вложения…"}
        </p>
      );
    case "ready":
      return (
        <div className={styles.status}>
          <p>{state.attachment.kind === "gif" ? "GIF добавлен" : "Фото добавлено"}</p>
          <ClearButton onClear={onClear} />
        </div>
      );
    case "failed":
      return (
        <div className={styles.status}>
          <p className={styles.error} role="alert">
            {failureText(state.error)}
          </p>
          {state.attachment && (
            <button type="button" className={styles.link} onClick={onRetry}>
              Повторить
            </button>
          )}
          <ClearButton onClear={onClear} />
        </div>
      );
  }
}

function ClearButton({ onClear }: { onClear: () => void }) {
  return (
    <button type="button" className={styles.link} aria-label="Убрать вложение" onClick={onClear}>
      Убрать
    </button>
  );
}

function failureText(error: unknown): string {
  if (!(error instanceof AttachmentError)) return "Не удалось загрузить фото";
  switch (error.problem) {
    case "gif_too_large":
      return "GIF больше 5 МБ";
    case "photo_too_large":
      return "Фото больше 5 МБ";
    case "unsupported":
      return "Этот формат файла не поддерживается";
  }
}
